import { useRef } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { FileText, RefreshCcw, Trash2 } from 'lucide-react'

interface DocumentCardProps {
  /** A fresh upload (File) or the summary of a server-persisted document
   *  rehydrated after a reload — only name and size are rendered. */
  file: { name: string; size: number }
  /** How many fields/rows the extraction actually returned. Omit (e.g. for a
   *  document rehydrated after a reload, where the count isn't known) to hide
   *  the "AI extracted …" line rather than show a fabricated number. */
  fieldCount?: number
  /** Called with the newly chosen file when the user swaps this document. */
  onReplace: (file: File) => void
  onRemove: () => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentCard({ file, fieldCount, onReplace, onRemove }: DocumentCardProps) {
  const reducedMotion = useReducedMotion()
  const replaceInputRef = useRef<HTMLInputElement>(null)

  return (
    <motion.article
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      transition={
        reducedMotion
          ? { duration: 0.2 }
          : { duration: 0.3, ease: [0.0, 0.0, 0.2, 1.0] }
      }
      aria-label={`Uploaded document: ${file.name}`}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing-4)',
        background: 'var(--color-surface-container-lowest)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-6)',
        border: '1px solid var(--color-outline-subtle)',
      }}
    >
      {/* Icon / thumbnail area */}
      <motion.div
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={
          reducedMotion
            ? { duration: 0.2 }
            : { duration: 0.2, ease: [0.0, 0.0, 0.2, 1.0], delay: 0.1 }
        }
        style={{
          width: 'var(--doc-card-thumbnail)',
          height: 'var(--doc-card-thumbnail)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-info-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden="true"
      >
        <FileText
          size={32}
          color="var(--color-action-primary)"
          strokeWidth={2}
        />
      </motion.div>

      {/* Info column */}
      <motion.div
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={
          reducedMotion
            ? { duration: 0.2 }
            : { duration: 0.15, ease: [0.0, 0.0, 0.2, 1.0], delay: 0.2 }
        }
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-1)',
        }}
      >
        {/* Filename */}
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={file.name}
        >
          {file.name}
        </p>

        {/* File size */}
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            fontWeight: 'var(--weight-regular)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {formatFileSize(file.size)}
        </p>

        {/* AI extracted fields badge — only when the real count is known. */}
        {fieldCount != null && (
          <span
            style={{
              display: 'inline-flex',
              alignSelf: 'flex-start',
              marginTop: 'var(--spacing-1)',
              background: 'var(--color-ai-badge-surface)',
              color: 'var(--color-action-primary)',
              borderRadius: 'var(--radius-full)',
              padding: '2px 8px',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-label-xs)',
              fontWeight: 'var(--weight-semibold)',
              letterSpacing: '0.02em',
            }}
          >
            AI extracted {fieldCount} field{fieldCount === 1 ? '' : 's'}
          </span>
        )}
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={
          reducedMotion
            ? { duration: 0.2 }
            : { duration: 0.15, ease: [0.0, 0.0, 0.2, 1.0], delay: 0.3 }
        }
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0,
          flexShrink: 0,
        }}
      >
        <input
          ref={replaceInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
          onChange={(e) => {
            const next = e.target.files?.[0]
            // Reset so picking the same filename again still fires onChange.
            e.target.value = ''
            if (next) onReplace(next)
          }}
        />
        <button
          onClick={() => replaceInputRef.current?.click()}
          aria-label={`Replace ${file.name}. This may update auto-filled fields`}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--color-action-primary)',
            minHeight: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)'
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-action-primary)'
          }}
        >
          <RefreshCcw size={16} strokeWidth={2} aria-hidden="true" />
          Replace
        </button>
        <button
          onClick={onRemove}
          aria-label={`Remove ${file.name}`}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-label-sm)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--color-error-text)',
            minHeight: 'var(--touch-target-min)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-error-text-hover)'
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-error-text)'
          }}
        >
          <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
          Remove
        </button>
      </motion.div>
    </motion.article>
  )
}
